/**
 * Phase 110 Plan 110-08 (Surface A) — ProjectDiscountConfig / ProjectDiscountTier API client.
 *
 * Mirrors `projectSlaTier.ts` verbatim in structure. Backend authorization
 * (D-08, defense-in-depth per checker I-18): `requireAdminOrProjectMemberByProject`
 * on both GET routes, `requireAdmin` on all four write routes — tighter than
 * the SLA-tier sibling's `requireAdminOrProjectPM`, because a discount is a
 * commercial agreement a PM may read but never change.
 *
 * Route paths verbatim from `110-04-SUMMARY.md`:
 *   GET    /projects/:projectId/discount-config
 *   PATCH  /projects/:projectId/discount-config
 *   GET    /projects/:projectId/discount-tiers
 *   POST   /projects/:projectId/discount-tiers
 *   PATCH  /projects/:projectId/discount-tiers/:tierId
 *   DELETE /projects/:projectId/discount-tiers/:tierId
 *
 * Field shapes mirror `backend/src/api/schemas/projectDiscount.ts` exactly.
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';

export type DiscountMode = 'none' | 'tiers' | 'manual';

export interface ProjectDiscountConfig {
  id: number;
  projectId: number;
  mode: DiscountMode;
  manualPercentage: number | null;
  retainerInBase: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDiscountTier {
  id: number;
  projectId: number;
  minHours: number;
  percentage: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertProjectDiscountConfigInput {
  mode?: DiscountMode;
  manualPercentage?: number | null;
  retainerInBase?: boolean;
}

export interface CreateProjectDiscountTierInput {
  minHours: number;
  percentage: number;
}

export type UpdateProjectDiscountTierInput = Partial<CreateProjectDiscountTierInput>;

export function fetchProjectDiscountConfig(projectId: number) {
  return apiGet<{ data: ProjectDiscountConfig }>(`/projects/${projectId}/discount-config`);
}

export function upsertProjectDiscountConfig(
  projectId: number,
  input: UpsertProjectDiscountConfigInput,
) {
  return apiPatch<{ data: ProjectDiscountConfig }>(
    `/projects/${projectId}/discount-config`,
    input,
  );
}

export function fetchProjectDiscountTiers(projectId: number) {
  return apiGet<{ data: ProjectDiscountTier[] }>(`/projects/${projectId}/discount-tiers`);
}

export function createProjectDiscountTier(
  projectId: number,
  input: CreateProjectDiscountTierInput,
) {
  return apiPost<{ data: ProjectDiscountTier }>(`/projects/${projectId}/discount-tiers`, input);
}

export function updateProjectDiscountTier(
  projectId: number,
  tierId: number,
  input: UpdateProjectDiscountTierInput,
) {
  return apiPatch<{ data: ProjectDiscountTier }>(
    `/projects/${projectId}/discount-tiers/${tierId}`,
    input,
  );
}

export function deleteProjectDiscountTier(projectId: number, tierId: number) {
  return apiDelete<void>(`/projects/${projectId}/discount-tiers/${tierId}`);
}
